import {
  Component,
  OnInit,
  ViewEncapsulation,
  OnChanges,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
  NgZone,
  HostListener
} from '@angular/core';
import { takeUntil } from 'rxjs/operators';

import { renderEnvironment } from 'environments/environment';
import { EcommerceConfigService } from 'idt-ecommerce-api/config/ecommerce-config.service';
import { ContextService } from 'idt-ecommerce-api/context/context.service';
import { UnsubscribeComponent } from 'shared/directives/unsubscribe.component';
import { DynamicIdtComponentService } from 'shared/dynamic/dynamic-idt-component.service';
import { renderComponents } from 'shared/idt-components/idt-components-render.module';
import { IdtComponentsService } from 'shared/idt-components/idt-components.service';
import { SharedConfigurationService } from 'shared/services/shared-configuration.service';

import { CMSEvent, CMSEventType } from './models/cms-event.model';
import { RenderEvent } from './render-events/render-events.model';
import { RenderEventsService } from './render-events/render-events.service';
import { RenderConfiguration } from './render.config';
import { CMSRenderEventsService } from './services/cms-render-events.service';
import { RenderConfigurationService } from './services/render-configuration.service';
import { RenderSlugService } from './services/render-slug.service';
import { RenderStateService } from './services/render-state/render-state.service';
import { TenantVariablesService } from './services/tenant-variables/tenant-variables.service';


const CMS_TENANT_VARIABLES_UPDATED = 'CMS_TENANT_VARIABLES_UPDATED';

@Component({
  selector: 'render-idt',
  template: `
  <render-page></render-page>
  `,
  /* https://jira.inditex.com/jira/browse/MECFRONT-1539
   * En caso de necesitar acceder a elementos del DOM desde fuera de Angular, será necesario retirar esta linea al
   * generar el web component
   * Esto se debe a que al hacer queySelector de un elemento interior, nunca será encontrado.
   * También se puede acceder sin eliminar la encapsulación a traves de
   * querySelector('render-idt).shadowRoot.querySelector('mi-elemento')
   * */
  encapsulation: ViewEncapsulation.ShadowDom,
  styleUrls: [
    './render-page.component.scss'
  ],
  providers: [RenderSlugService]
})
export class RenderComponent extends UnsubscribeComponent implements OnInit, OnChanges, OnDestroy {

  @Input() public slug!: string;
  @Input() public country!: string;
  @Input() public versionid!: string;
  @Input() public lang!: string;
  @Input() public userflags: string | undefined;
  @Input() public timetravelto: string | undefined;
  @Input() public config?: string;
  @Input() public timestamp?: string;
  @Output() public idtEvent = new EventEmitter<RenderEvent>();
  @Output() public cmsEvent = new EventEmitter<CMSEvent<CMSEventType>>();

  constructor(
    private readonly contextService: ContextService,
    private readonly ecommerceConfigService: EcommerceConfigService,
    private readonly tenantVariablesService: TenantVariablesService,
    private readonly idtComponentsService: IdtComponentsService,
    private readonly dynamicIdtComponentService: DynamicIdtComponentService,
    private readonly renderConfigurationService: RenderConfigurationService,
    private readonly renderStateService: RenderStateService,
    private readonly sharedConfigurationService: SharedConfigurationService,
    private readonly renderEventsService: RenderEventsService,
    private readonly cmsRenderEventsService: CMSRenderEventsService,
    private readonly ngZone: NgZone,
    private readonly renderSlugService: RenderSlugService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.parseTenantVars();

    this.renderEventsService.events
      .pipe(
        takeUntil(this.clearSubscriptions$)
      )
      .subscribe((linkEvent) => {
        linkEvent.event.preventDefault();
        this.idtEvent.next(linkEvent);
      });

    this.cmsRenderEventsService.cmsEvent
      .pipe(
        takeUntil(this.clearSubscriptions$)
      )
      .subscribe((event) => {
        this.cmsEvent.next(event);
      });

    this.dynamicIdtComponentService.setComponents(renderComponents);
    this.dynamicIdtComponentService.setDefinitions(this.idtComponentsService.componentsDefinition);
    this.renderSlugService.setSlug(this.slug);

    if (this.timestamp) {
      this.contextService.setStoreTimestamp(this.timestamp);
    }
  }

  public ngOnChanges(): void {
    this.renderConfigurationService.config = this.config === undefined
      ? renderEnvironment
      : JSON.parse(this.config) as RenderConfiguration;
    this.sharedConfigurationService.config = this.renderConfigurationService.config;
    this.ecommerceConfigService.setConfig(this.renderConfigurationService.config.ecommerce);
    this.ngZone.run(async () => {
      await this.renderStateService.initPage({
        slug: this.slug,
        country: this.country,
        versionId: this.versionid,
        lang: this.lang,
        timeTravelTo: this.timetravelto,
        userFlags: this.userflags
      });
      this.contextService.init(this.country, this.lang);
    });
  }

  @HostListener(`window:${CMS_TENANT_VARIABLES_UPDATED}`)
  private syncTenantVars(): void {
    this.parseTenantVars();
  }

  private parseTenantVars(): void {
    const parsedVariables = localStorage.getItem('CMS_TENANT_VARIABLES') ?? '{}';
    this.tenantVariablesService.setTenantVariables(JSON.parse(parsedVariables) as Record<string, string>);
  }

}
